import { Link } from 'react-router';
import { ROUTES } from '@/constants';
import {
  useCurrentUser,
  useDownloadInstaller,
  useLatestVersion,
  useLicence,
} from '@/services/queries';
import type { Licence, SoftwareVersion } from '@/types';
import { Skeleton } from '@/shared/ui/skeleton';
import heroArt from '@/shared/assets/dashboard-hero.webp';
import { LicenceKeyReveal } from './LicenceKeyReveal';

/**
 * The dashboard's opening banner, transcribed from the design.
 *
 * ── Why this is one component and not four ──────────────────────────────────
 *
 * Because the design draws one object: a blue slab with a hairline down the
 * middle, licence on the left and software on the right, sharing a shadow and a
 * corner radius. Splitting it would mean two components negotiating a divider.
 *
 * ── The measurements ────────────────────────────────────────────────────────
 *
 * 20px radius, 34px/40px padding, a 52px avatar, a 32px greeting at -0.5px
 * tracking, and the two panels weighted 1.9 to 1.3 with 380px and 320px bases.
 * All transcribed. The oddest-looking one - a shadow at 0 18px 40px in the
 * brand colour at 28% - is what makes the slab sit ON the page rather than in
 * it, and a generic grey shadow at the same offset reads as a mistake.
 *
 * ── Everything here is real ─────────────────────────────────────────────────
 *
 * The design's own numbers are sample data. These come from
 * GET /api/user/licence and GET /api/user/software/latest. Where the design
 * shows something we do not store - it says "last sync 12 minutes ago", and
 * nothing records a lab-wide sync time - the field is left out rather than
 * filled with a plausible number. A dashboard that invents one figure is a
 * dashboard nobody checks the others on.
 */

/** The design's white-on-blue pills, which are not the page's status pills. */
function BannerPill({ children, solid = false }: { children: React.ReactNode; solid?: boolean }) {
  return (
    <span
      className={
        solid
          ? 'inline-flex w-[110px] items-center justify-center rounded-full bg-white px-3 py-[5px] text-xs font-semibold whitespace-nowrap text-brand-600'
          : 'inline-flex w-[110px] items-center justify-center gap-1.5 rounded-full bg-white/20 px-3 py-[5px] text-xs font-semibold whitespace-nowrap text-white'
      }
    >
      {children}
    </span>
  );
}

/** Label over value, mono, as the design sets the three date columns. */
function DateStat({ label, value, tint }: { label: string; value: string; tint?: string }) {
  return (
    <div>
      <div className="mb-1.5 text-[12.5px] text-white/60">{label}</div>
      <div className={`font-mono text-[15px] font-semibold ${tint ?? 'text-white'}`}>{value}</div>
    </div>
  );
}

/** "14 Mar 2027", as the design writes dates. Never a raw ISO string. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The right-hand software panel.
 *
 * ── Why "installed" is not shown ────────────────────────────────────────────
 *
 * The design's panel reads "NuCad 4.1.4 installed" above "4.2.1 is available".
 * We cannot know what is installed: nothing in this portal talks to the
 * workstation, and the desktop reports its version on its own update check-in,
 * against a device rather than against a browser session.
 *
 * So this states what IS knowable - the newest published build - and does not
 * claim to know what the person reading it is running. Deriving an "installed"
 * version from the newest release would make the comparison always say "up to
 * date", which is worse than not saying.
 */
function SoftwarePanel({
  version,
  linux,
}: {
  version: SoftwareVersion | null;
  /** The newest Linux build, when there is one: offered beside the Windows download. */
  linux: SoftwareVersion | null;
}) {
  const download = useDownloadInstaller();

  return (
    <div className="relative min-w-0 flex-[1.3_1_320px] overflow-hidden">
      {/*
        The designer's artwork - a blue-tinted molar cross-section, extracted
        from the design file itself. Its edges are alpha-faded, which is why it
        is a WebP with transparency rather than a JPEG: it is meant to dissolve
        into the blue rather than sit in a rectangle on it.

        aria-hidden with an empty alt: it is atmosphere. Describing it to a
        screen reader would announce a tooth diagram in the middle of a licence.
      */}
      <img
        src={heroArt}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 size-full object-cover opacity-90"
        style={{ objectPosition: '60% 45%' }}
      />

      <div className="relative z-[1] flex h-full flex-col justify-between gap-6 px-[30px] py-[34px]">
        <div className="flex items-start justify-between gap-3">
          <span className="text-[12.5px] text-white/75">Software version</span>
          <span className="text-right whitespace-nowrap">
            <span className="font-mono text-[17px] font-bold text-white">
              NµCAD {version?.versionNumber ?? '—'}
            </span>{' '}
            <span className="text-[12.5px] text-white/75">latest</span>
          </span>
        </div>

        {version ? (
          <div className="flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
            <div className="min-w-0">
              <div className="mb-2 text-[13.5px] font-semibold text-white">
                Version <span className="font-mono">{version.versionNumber}</span> is available
              </div>
              <span className="text-[12px] text-white/70">
                Published {formatDate(version.publishedAt)}
                {version.mandatory ? ' · required' : ''}
              </span>
            </div>
            <div className="max-w-[220px] text-right">
              <button
                type="button"
                disabled={download.isPending}
                onClick={() => {
                  download.mutate('windows');
                }}
                className="mb-2.5 h-9 rounded-lg bg-white px-4 text-[13px] font-semibold whitespace-nowrap text-brand-600 hover:bg-brand-50 disabled:opacity-70"
              >
                {download.isPending ? 'Preparing…' : 'Download'}
              </button>
              {linux && (
                <button
                  type="button"
                  disabled={download.isPending}
                  onClick={() => {
                    download.mutate('linux');
                  }}
                  className="mb-2.5 block w-full text-right text-[12px] font-medium text-white/85 underline-offset-2 hover:text-white hover:underline disabled:opacity-70"
                >
                  Also for Linux {linux.versionNumber}
                </button>
              )}
              {version.releaseNotes && (
                <div className="line-clamp-3 text-[11.5px] leading-[1.5] text-white/75">
                  {version.releaseNotes}
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-[12.5px] leading-relaxed text-white/75">
            No build has been published yet. The installer appears here as soon as one is released.
          </p>
        )}
      </div>
    </div>
  );
}

/** The seat count as the design words it: "6 Seats", or "1 Seat". */
function seatWord(n: number): string {
  return `${String(n)} ${n === 1 ? 'Seat' : 'Seats'}`;
}

/**
 * The licence's overall state, derived rather than stored.
 *
 * There is no `licences.status` column, and deliberately so - a licence's life
 * is described by its seats' expiry dates. So the banner computes it, and the
 * threshold for "Expiring soon" is 60 days: long enough that a lab has time to
 * raise a purchase order, short enough not to be permanently amber.
 */
function licenceState(licence: Licence): { label: string; tone: 'ok' | 'warn' | 'bad' } {
  if (!licence.expiresAt) return { label: 'Not activated', tone: 'warn' };

  const daysLeft = Math.ceil((new Date(licence.expiresAt).getTime() - Date.now()) / 86_400_000);
  if (daysLeft < 0) return { label: 'Expired', tone: 'bad' };
  if (daysLeft <= 60) return { label: 'Expiring soon', tone: 'warn' };
  return { label: 'Active', tone: 'ok' };
}

/**
 * The two modes `license_mode` actually has.
 *
 * Only these two exist - there is no enterprise tier in the schema - so the
 * fallback below is for a mode added later, not for a case that exists now.
 */
const MODE_LABEL: Record<string, string> = {
  single_seat: 'Single Seat',
  lab_seat: 'Lab Seat',
};

const DOT_CLASS: Record<'ok' | 'warn' | 'bad', string> = {
  ok: 'bg-emerald-300',
  warn: 'bg-amber-300',
  bad: 'bg-red-300',
};

const EXPIRY_TINT: Record<'ok' | 'warn' | 'bad', string> = {
  ok: 'text-white',
  warn: 'text-amber-200',
  bad: 'text-red-200',
};

export function LicenceHero() {
  const { data: user } = useCurrentUser();
  const licenceQuery = useLicence();
  const versionQuery = useLatestVersion('windows');
  const linuxQuery = useLatestVersion('linux');

  if (licenceQuery.isPending) return <Skeleton className="h-[340px] w-full rounded-[20px]" />;

  const licence = licenceQuery.data?.licence ?? null;
  const firstName = user?.name.split(' ')[0] ?? '';

  // No licence is an onboarding state, not an error. The same slab, so the page
  // does not reflow once one is issued - just without the numbers.
  if (!licence) {
    return (
      <div className="rounded-[20px] bg-brand-600 px-10 py-[34px] shadow-[0_18px_40px_rgba(59,99,224,0.28)]">
        <div className="text-[32px] leading-[1.2] font-semibold tracking-[-0.5px] text-white">
          Welcome{firstName && `, ${firstName}`}
        </div>
        <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-white/75">
          Your lab does not have a licence yet. Once one is issued it appears here with your seats,
          your key and the installer.
        </p>
        <Link
          to={ROUTES.issues}
          className="mt-5 inline-flex h-9 items-center rounded-lg bg-white px-4 text-[13px] font-semibold text-brand-600 hover:bg-brand-50"
        >
          Ask about licensing
        </Link>
      </div>
    );
  }

  const state = licenceState(licence);
  const yourSeat = licence.seats.find((seat) => seat.isYou);

  return (
    <div className="overflow-hidden rounded-[20px] bg-brand-600 shadow-[0_18px_40px_rgba(59,99,224,0.28)]">
      <div className="flex flex-col items-stretch lg:flex-row">
        <div className="flex min-w-0 flex-col px-10 py-[34px] lg:flex-[1.9_1_380px]">
          <div className="flex items-center gap-[18px]">
            <div
              className="flex size-[52px] flex-none items-center justify-center rounded-full bg-white/20"
              aria-hidden="true"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-[32px] leading-[1.2] font-semibold tracking-[-0.5px] text-white">
                Welcome back{firstName && `, ${firstName}`}
              </div>
              <div className="mt-1.5 text-[14.5px] text-white/75">
                {licence.organizationName} · {String(licence.seatsClaimed)} of{' '}
                {String(licence.seatCount)} seats bound
              </div>
            </div>
          </div>

          <div className="my-5 h-px bg-white/30" />

          <div className="grid gap-x-[26px] gap-y-5 sm:grid-cols-[minmax(140px,auto)_minmax(140px,auto)_1fr]">
            <div>
              <div className="mb-2 text-[13.5px] text-white/70">Your licence</div>
              <BannerPill>
                <span
                  className={`size-1.5 rounded-full ${DOT_CLASS[state.tone]}`}
                  aria-hidden="true"
                />
                {state.label}
              </BannerPill>
            </div>
            <div>
              <div className="mb-2 text-[13.5px] text-white/70">
                {MODE_LABEL[licence.mode] ?? licence.mode}
              </div>
              <BannerPill solid>{seatWord(licence.seatCount)}</BannerPill>
            </div>
            <LicenceKeyReveal masked={yourSeat?.licenseKeyMasked} />
          </div>

          {/* Pushes the dates to the bottom, so both panels are the same height
              whatever the software panel has to say. */}
          <div className="flex-1" />

          <div className="my-[18px] h-px bg-white/30" />

          <div className="grid gap-x-[26px] gap-y-4 sm:grid-cols-[minmax(140px,auto)_minmax(140px,auto)_1fr]">
            <DateStat label="Purchased" value={formatDate(licence.issuedAt)} />
            <DateStat label="Activated" value={formatDate(licence.activatedAt)} />
            <DateStat
              label="Expires"
              value={formatDate(licence.expiresAt)}
              tint={EXPIRY_TINT[state.tone]}
            />
          </div>
        </div>

        <div className="w-px flex-none bg-white/30" aria-hidden="true" />

        <SoftwarePanel
          version={versionQuery.data?.version ?? null}
          linux={linuxQuery.data?.version ?? null}
        />
      </div>
    </div>
  );
}
